import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Button from '@components/Button';
import { useEventTracking } from '@hooks';
import { ClickTarget, click } from '@tracking';
import { WIDTH_XS } from '@utils/constants';
import { convertMDLinkToHTML } from '@utils/formatting';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

const MOBILE_LINE_LIMIT = 3;
const DESKTOP_LINE_LIMIT = 5;
const SHOW_LESS = 'Show less';
const READ_MORE = 'Read more';

interface ProfileDetailsProps {
  bio: string;
  name: string;
}

const ProfileDetails = ({ bio, name }: ProfileDetailsProps) => {
  const { trackEvent } = useEventTracking();

  const [expanded, setExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  bio = useMemo(() => {
    return AuthoredContentReactToHtmlParser(convertMDLinkToHTML(bio));
  }, [bio]);

  useEffect(() => {
    const checkContentHeight = () => {
      if (!contentRef.current) return;

      const content = contentRef.current;
      const lineHeight = parseInt(window.getComputedStyle(content).lineHeight);
      const maxLines = window.innerWidth >= WIDTH_XS ? DESKTOP_LINE_LIMIT : MOBILE_LINE_LIMIT;
      const maxHeight = lineHeight * maxLines;

      setShowExpandButton(content.scrollHeight > maxHeight);
    };

    checkContentHeight();
    window.addEventListener('resize', checkContentHeight, { passive: true });

    return () =>
      window.removeEventListener('resize', checkContentHeight, {
        passive: true,
      } as EventListenerOptions);
  }, []);

  return (
    <div className="ml-8 grid grid-cols-1 gap-24 border-0 pb-32 pt-16 xs:grid-cols-5 xs:pr-16 sm:col-span-6 sm:block sm:grid-cols-none sm:border-l sm:border-solid sm:border-grayscale-beige sm:p-32 sm:pr-32 md:col-span-7">
      <div className="xs:col-start-2 xs:col-end-6">
        <p
          ref={contentRef}
          className={clsx('m-0 font-suisse-neue text-body-md text-primary-black', {
            'line-clamp-3 scroll-mt-[12rem] xs:line-clamp-5': !expanded,
          })}
        >
          {bio}
        </p>
        {showExpandButton && (
          <Button
            as="button"
            className="expand-btn mt-8 touch-manipulation border-none bg-transparent p-0 font-suisse-intl text-button-md underline hover:text-primary-magenta md:mt-24"
            onClick={() => setExpanded((prevExpanded) => !prevExpanded)}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickTarget: expanded ? ClickTarget.ShowLess : ClickTarget.ReadMore,
                  clickText: name,
                })
              )
            }
          >
            {expanded ? SHOW_LESS : READ_MORE}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProfileDetails;
